/**
 * Minha Conta (dono, 2026-10-17): o "Resumo" e o "Gerenciar Conta" do Liberty numa pagina so'.
 * Cabecalho com os dados da conta + acoes pequenas (alterar senha por codigo, resetar sub-senha) + os cards do resumo
 * (sem o cartao presente e sem o link de indicacao). Mesmas APIs: /Account/get (AuthContext; traz o saldo de gema),
 * /Account/premium (principal + adicional), /Character/get, /Account/forgotPassword (codigo pelo e-mail),
 * /Account/resetSub.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { CalendarDays, Clock, Crown, Gem, IdCard, KeyRound, Loader2, Mail, MailCheck, ShieldOff, Swords, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { getCharacters, type Character } from '@/shared/services/characterService';
import { forgotPassword } from '@/shared/services/authServices';
import { resetSub } from '@/shared/services/accountService';
import { premiumLabel } from '@/shared/lib/premiumTiers';
import { NATION_NAME, useNextWar } from '@/shared/lib/war';
import { OtpInput } from '@/features/Cadastro/CadastroParts';
import { NxModal, errMsg, fmtDate, fmtDateTime } from './NxModal';

function Stat({ icon: Icon, label, value, note }: { icon: LucideIcon; label: string; value: string; note: string }) {
  return (
    <article className="nxd-stat">
      <span className="nxd-stat__icon">
        <Icon size={20} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{note}</p>
      </div>
    </article>
  );
}

export default function MyAccountPage() {
  const { account, premium } = useAuth();
  const war = useNextWar();
  const [chars, setChars] = useState<Character[] | null>(null);
  const [modal, setModal] = useState<'password' | 'sub' | null>(null);

  useEffect(() => {
    getCharacters().then(setChars).catch(() => setChars([]));
  }, []);

  // principal + adicional: "Platina/Prata" (dono 2026-10-17)
  const tier = premiumLabel(premium);
  // nacao = a do personagem de maior nivel
  const main = useMemo(
    () => (chars ?? []).reduce<Character | null>((best, c) => (!best || c.lev > best.lev ? c : best), null),
    [chars]
  );

  if (!account)
    return (
      <div className="nxd-loading">
        <Loader2 size={26} className="nxb-spin" />
      </div>
    );

  const name = account.userName?.trim() || account.id;
  const tags = [
    chars ? `${chars.length} ${chars.length === 1 ? 'personagem' : 'personagens'}` : null,
    tier.isPaid ? `Premium ${tier.name}` : 'Sem premium',
    NATION_NAME[main?.nation ?? 0] || 'Sem nação',
  ].filter(Boolean) as string[];

  return (
    <div className="nxd-stack">
      <header className="nxd-heading">
        <p className="nx-eyebrow">Painel do jogador</p>
        <h1>Minha Conta</h1>
      </header>

      <section className="nxd-card nxd-profile">
        <span className="nxd-profile__avatar">{name[0]?.toUpperCase()}</span>
        <div className="nxd-profile__main">
          <h2>
            {name}
            {account.isGM && <span className="nxd-tag nxd-tag--gm">GM</span>}
          </h2>
          <ul className="nxd-facts">
            <li>
              <IdCard size={15} /> ID <b>{account.id}</b>
            </li>
            <li>
              <Mail size={15} /> <b>{account.email || 'Sem e-mail'}</b>
            </li>
            <li>
              <CalendarDays size={15} /> Conta criada em <b>{fmtDate(account.createdAt ?? account.createDate)}</b>
            </li>
            <li>
              <Clock size={15} /> Último acesso <b>{fmtDateTime(account.lastLoginAt ?? account.loginTime)}</b>
            </li>
          </ul>
          <div className="nxd-tags">
            {tags.map((t) => (
              <span key={t} className="nxd-tag">
                {t}
              </span>
            ))}
          </div>
        </div>
        <div className="nxd-profile__actions">
          <button type="button" className="nxd-btn" onClick={() => setModal('password')}>
            <KeyRound size={15} /> Alterar senha
          </button>
          <button type="button" className="nxd-btn" onClick={() => setModal('sub')}>
            <ShieldOff size={15} /> Resetar sub-senha
          </button>
        </div>
      </section>

      <section className="nxd-stats">
        <Stat icon={Users} label="Personagens" value={chars ? String(chars.length) : '…'} note="criados na conta" />
        {/* no Next o saldo e' de GEMA (cabal_forcegem_table), nao o cash do site do Liberty */}
        <Stat icon={Gem} label="Saldo de Gema" value={(account.forceGem ?? 0).toLocaleString('pt-BR')} note="na conta" />
        <Stat
          icon={Crown}
          label="Premium"
          value={tier.name.replace(/\//g, '/​')}
          note={tier.isPaid ? `${tier.days} dias restantes` : 'sem premium ativo'}
        />
        <Stat icon={Swords} label="Próxima guerra" value={war.short} note="Capella vs Procyon" />
      </section>

      <ChangePasswordModal open={modal === 'password'} contact={account.id} onClose={() => setModal(null)} />
      <ResetSubModal open={modal === 'sub'} onClose={() => setModal(null)} />
    </div>
  );
}

/** Alterar senha = o mesmo /forgotPassword do "esqueci", com o ID da conta logada: codigo no e-mail -> senha nova. */
function ChangePasswordModal({ open, contact, onClose }: { open: boolean; contact: string; onClose: () => void }) {
  const [step, setStep] = useState<'start' | 'code' | 'pass' | 'done'>('start');
  const [code, setCode] = useState('');
  const [pass, setPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [dest, setDest] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const close = () => {
    setStep('start');
    setCode('');
    setPass('');
    setConfirm('');
    setError(null);
    onClose();
  };

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setLoading(true);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(errMsg(e, fallback));
    } finally {
      setLoading(false);
    }
  };

  const sendCode = () =>
    run(async () => {
      const res: any = await forgotPassword({ contact });
      setDest(String(res?.Destination ?? res?.destination ?? ''));
      setCooldown(Number(res?.RetryAfterSeconds ?? res?.retryAfterSeconds ?? 60));
      setCode('');
      setStep('code');
    }, 'Não foi possível enviar o código.');

  const checkCode = (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) return setError('Digite o código completo (6 dígitos).');
    void run(async () => {
      await forgotPassword({ contact, code });
      setStep('pass');
    }, 'Código inválido ou expirado.');
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    if (pass.length < 6) return setError('A senha deve ter no mínimo 6 caracteres.');
    if (pass !== confirm) return setError('As senhas não conferem.');
    void run(async () => {
      await forgotPassword({ contact, code, newPassword: pass });
      setStep('done');
    }, 'Não foi possível alterar a senha.');
  };

  return (
    <NxModal open={open} onClose={close} busy={loading} className="nxb-modal--login">
      {step === 'start' && (
        <div>
          <span className="nxb-badge">
            <KeyRound size={28} />
          </span>
          <h2 className="nxb-title nxb-title--center">
            Alterar <span>senha</span>
          </h2>
          <p className="nxb-lead nxb-lead--center">
            Vamos enviar um código para o e-mail da conta. Com ele você define a senha nova (vale para o site e o jogo).
          </p>
          {error && <p className="nxb-error">{error}</p>}
          <button type="button" className="nxb-submit" onClick={() => void sendCode()} disabled={loading}>
            {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
            {loading ? 'Enviando...' : 'Enviar código'}
          </button>
        </div>
      )}
      {step === 'code' && (
        <form onSubmit={checkCode} noValidate>
          <span className="nxb-badge">
            <MailCheck size={30} />
          </span>
          <h2 className="nxb-title nxb-title--center">
            Confirme o <span>código</span>
          </h2>
          <p className="nxb-lead nxb-lead--center">
            Enviamos um código de 6 dígitos para <b>{dest}</b>.<small>Não chegou? Confira a caixa de spam.</small>
          </p>
          <div className="nxb-otp">
            <OtpInput value={code} onChange={setCode} disabled={loading} length={6} />
          </div>
          <div className="nxb-links nxb-links--end">
            <button type="button" onClick={() => void sendCode()} disabled={loading || cooldown > 0}>
              {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
            </button>
          </div>
          {error && <p className="nxb-error">{error}</p>}
          <button type="submit" className="nxb-submit" disabled={loading || code.length !== 6}>
            {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
            {loading ? 'Validando...' : 'Confirmar código'}
          </button>
        </form>
      )}
      {step === 'pass' && (
        <form onSubmit={save} noValidate>
          <h2 className="nxb-title">
            Nova <span>senha</span>
          </h2>
          <p className="nxb-lead">Escolha a senha nova da sua conta.</p>
          <label className="nxb-field">
            <span>Nova senha</span>
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} autoComplete="new-password" />
          </label>
          <label className="nxb-field">
            <span>Confirmar senha</span>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </label>
          {error && <p className="nxb-error">{error}</p>}
          <button type="submit" className="nxb-submit" disabled={loading}>
            {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
            {loading ? 'Salvando...' : 'Salvar senha'}
          </button>
        </form>
      )}
      {step === 'done' && (
        <div className="nxb-done">
          <h2 className="nxb-title nxb-title--center">
            Senha <span>alterada!</span>
          </h2>
          <p className="nxb-lead nxb-lead--center">Use a senha nova na próxima vez que entrar no jogo ou no site.</p>
          <button type="button" className="nxb-submit" onClick={close}>
            Fechar
          </button>
        </div>
      )}
    </NxModal>
  );
}

/** Resetar sub-senha: confirma a senha da conta e apaga a sub-senha (o jogo pede uma nova na proxima entrada). */
function ResetSubModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const close = () => {
    setPassword('');
    setError(null);
    setDone(false);
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password) return setError('Informe a senha da conta.');
    setLoading(true);
    setError(null);
    try {
      await resetSub(password);
      setPassword('');
      setDone(true);
    } catch (err: any) {
      setError(errMsg(err, 'Não foi possível resetar a sub-senha.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <NxModal open={open} onClose={close} busy={loading} className="nxb-modal--login">
      {done ? (
        <div className="nxb-done">
          <h2 className="nxb-title nxb-title--center">
            Sub-senha <span>resetada!</span>
          </h2>
          <p className="nxb-lead nxb-lead--center">Na próxima entrada no jogo, crie uma sub-senha nova.</p>
          <button type="button" className="nxb-submit" onClick={close}>
            Fechar
          </button>
        </div>
      ) : (
        <form onSubmit={submit} noValidate>
          <span className="nxb-badge">
            <ShieldOff size={28} />
          </span>
          <h2 className="nxb-title nxb-title--center">
            Resetar <span>sub-senha</span>
          </h2>
          <p className="nxb-lead nxb-lead--center">
            Confirme a senha da conta. A sub-senha atual é apagada e o jogo pede uma nova na próxima entrada.
          </p>
          <label className="nxb-field">
            <span>Senha da conta</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </label>
          {error && <p className="nxb-error">{error}</p>}
          <button type="submit" className="nxb-submit" disabled={loading}>
            {loading ? <Loader2 size={18} className="nxb-spin" /> : null}
            {loading ? 'Resetando...' : 'Resetar sub-senha'}
          </button>
        </form>
      )}
    </NxModal>
  );
}
